<template>
  <div class="repair-detail-container">
    <div class="filter-bar">
      <el-date-picker
        v-model="selectedMonth"
        type="month"
        placeholder="选择月份"
        format="yyyy-MM"
        value-format="yyyy-MM"
        @change="handleDateChange"
      ></el-date-picker>
      
      <el-input
        v-model="searchContent"
        placeholder="搜索维修内容"
        clearable
        style="width: 300px; margin-left: 20px"
        @clear="handleSearch"
        @input="handleSearchDebounced"
      >
        <template #append>
          <el-button icon="el-icon-search" @click="handleSearch"></el-button>
        </template>
      </el-input>
      
      <!-- 将按钮类型改为success(绿色)，并添加ml-auto类 -->
      <el-button 
        type="success" 
        icon="el-icon-download" 
        @click="handleExport"
        :disabled="multipleSelection.length === 0"
        class="export-btn"
      >
        导出选中项
      </el-button>
    </div>

    <!-- 其他内容保持不变 -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">正在加载数据...</span>
    </div>

    <div v-else-if="filteredRepairList.length === 0 && !isLoading" class="no-data">
      <el-empty description="暂无维修记录数据"></el-empty>
    </div>

    <el-table
      v-else
      ref="multipleTable"
      :data="filteredRepairList"
      border
      tooltip-effect="dark"
      style="width: 100%; margin-top: 20px"
      @selection-change="handleSelectionChange"
    >
      <!-- 表格内容保持不变 -->
      <el-table-column
        type="selection"
        width="55"
        :selectable="isSelectable"
      ></el-table-column>
      
      <el-table-column
        type="index"
        label="序号"
        width="100"
        align="center"
        :index="indexMethod"
      ></el-table-column>
      
      <el-table-column
        prop="driverName"
        label="司机姓名"
        width="120"
        align="center"
      ></el-table-column>
      
      <el-table-column
        prop="content"
        label="维修内容"
        width="150"
        align="center"
      ></el-table-column>
      
      <el-table-column
        prop="quantity"
        label="数量"
        width="100"
        align="center"
      ></el-table-column>
      
      <el-table-column
        prop="price"
        label="单价"
        width="100"
        align="center"
        :formatter="formatCurrency"
      ></el-table-column>
      
      <el-table-column
        label="总价"
        width="100"
        align="center"
      >
        <template #default="scope">
          {{ formatTotal(scope.row) }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="payee"
        label="收款人"
        width="120"
        align="center"
      ></el-table-column>
      
      <el-table-column
        prop="date"
        label="日期"
        width="150"
        align="center"
      ></el-table-column>
      
      <el-table-column
        label="图片"
        align="center"
      >
        <template #default="scope">
          <div class="demo-image__preview">
            <el-image v-if="scope.row.url"
              style="width: 100px; height: 100px"
              :src="scope.row.url" 
              :preview-src-list="[scope.row.url]">
            </el-image>
            <div v-else class="no-image">无图片</div>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import adminrequest from '@/utils/adminrequest'

export default {
  data() {
    return {
      selectedMonth: null,
      repairList: [],          // 原始维修记录数据
      filteredRepairList: [],  // 过滤后的维修记录数据
      searchContent: "",       // 搜索内容
      userId: null,            // 用户ID
      isLoading: false,        // 加载状态
      multipleSelection: [],   // 选中的记录
      searchTimer: null        // 搜索防抖计时器
    };
  },
  mounted() {
    // 从路由参数获取用户ID
    this.userId = this.$route.query.userId;
    
    // 设置默认选中当前月份
    const now = new Date();
    this.selectedMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    
    // 加载数据
    if (this.userId) {
      this.fetchData();
    } else {
      this.$message.warning("未获取到用户ID，请检查URL参数");
    }
  },
  methods: {
    // 序号生成方法
    indexMethod(index) {
      return index + 1;
    },
    
    // 日期变更处理
    handleDateChange(date) {
      this.selectedMonth = date;
      if (this.userId && date) {
        this.fetchData();
      }
    },
    
    // 获取维修记录数据
    fetchData() {
      if (!this.userId || !this.selectedMonth) {
        return;
      }
      
      this.isLoading = true;
      
      const options = {
        method: 'GET',
        url: '/admin/manage/repair/getRepairDetail',
        params: {
          userId: this.userId,
          date: this.selectedMonth
        }
      };
      
      adminrequest.request(options)
        .then(response => {
          this.isLoading = false;
          if (response.data.code === 200) {
            this.repairList = response.data.data || [];
            this.handleSearch(); // 触发一次搜索过滤
          } else {
            this.$message.error(`获取数据失败: ${response.data.message || '未知错误'}`);
          }
        })
        .catch(error => {
          this.isLoading = false;
          console.error("请求错误:", error);
          this.$message.error("请求失败，请检查网络或服务是否正常");
        });
    },
    
    // 格式化货币显示
    formatCurrency(row) {
      return `¥${Number(row.price || 0).toFixed(2)}`;
    },
    
    // 计算并格式化总价
    formatTotal(row) {
      const total = Number(row.price || 0) * Number(row.quantity || 0);
      return `¥${total.toFixed(2)}`;
    },
    
    // 判断是否可选中（必须包含有效的repairId）
    isSelectable(row) {
      // 确保行数据存在且包含有效的repairId
      return !!row && !!row.repairId && String(row.repairId).trim() !== '';
    },
    
    // 处理选择变化
    handleSelectionChange(val) {
      // 过滤出包含有效repairId的选中项
      this.multipleSelection = val.filter(item => 
        item && item.repairId && String(item.repairId).trim() !== ''
      );
    },
    
    // 处理搜索
    handleSearch() {
      if (!this.searchContent.trim()) {
        // 搜索内容为空时显示所有数据
        this.filteredRepairList = [...this.repairList];
      } else {
        // 根据维修内容进行模糊搜索
        const searchStr = this.searchContent.trim().toLowerCase();
        this.filteredRepairList = this.repairList.filter(item => 
          item.content && item.content.toLowerCase().includes(searchStr)
        );
      }
    },
    
    // 搜索防抖处理
    handleSearchDebounced() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.handleSearch();
      }, 300);
    },
    
    // 处理导出功能
    handleExport() {
      if (this.multipleSelection.length === 0) {
        this.$message.warning('请先选择要导出的记录');
        return;
      }
      
      // 提取选中项的repairId组成数组
      const repairIds = this.multipleSelection.map(item => item.repairId);
      console.log('导出的repairId列表:', repairIds);
      
      this.isLoading = true;
      
      // 准备导出请求参数，添加参数序列化配置
      const options = {
        method: 'GET',
        url: '/admin/manage/repair/getExcel',
        params: { 
          repairIds: repairIds  // 直接传递repairId数组
        },
        // 自定义参数序列化，将数组转为repairIds=5&repairIds=9格式
        paramsSerializer: params => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                // 数组参数：重复参数名
                return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
              }
              // 非数组参数：正常拼接
              return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            })
            .join('&');
        },
        responseType: 'blob'  // 接收二进制文件
      };
      
      adminrequest.request(options)
        .then(response => {
          this.isLoading = false;
          
          // 处理Excel文件下载
          const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          // 从响应头获取文件名
          let fileName = '维修记录.xlsx';
          const contentDisposition = response.headers['content-disposition'];
          if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches && matches[1]) {
              fileName = decodeURIComponent(matches[1].replace(/['"]/g, ''));
            }
          }
          
          // 创建下载链接并触发下载
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          
          // 清理资源
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          this.$message.success('Excel文件导出成功');
        })
        .catch(error => {
          this.isLoading = false;
          console.error('导出失败:', error);
          
          // 错误提示
          if (error.response && error.response.status === 400) {
            this.$message.error('导出失败：参数错误，请检查选中的记录');
          } else if (error.response && error.response.status === 401) {
            this.$message.error('导出失败：未授权，请重新登录');
          } else if (error.response && error.response.status === 404) {
            this.$message.error('导出失败：接口不存在，请检查URL是否正确');
          } else {
            this.$message.error('导出失败，请稍后重试');
          }
        });
    }
  },
  // 组件销毁前清理计时器
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }
};
</script>

<style scoped>
.repair-detail-container {
  padding: 20px;
  background-color: #fff;
  min-height: 100vh;
}

.filter-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%; /* 确保占据整行宽度 */
}

/* 导出按钮样式 */
.export-btn {
  margin-left: auto !important; /* 自动填充左侧空间，将按钮推到右侧 */
}

::v-deep .el-date-editor.el-input {
  width: 200px;
}

.loading-container {
  text-align: center;
  padding: 50px 0;
  color: #666;
  min-height: 100px;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #ddd;
  border-radius: 50%;
  border-top-color: #409eff;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;
  vertical-align: middle;
}

.loading-text {
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.no-data {
  margin-top: 20px;
  text-align: center;
}

.no-image {
  width: 100px;
  height: 100px;
  border: 1px dashed #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;
}

::v-deep .demo-image__preview {
  cursor: pointer;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  
  /* 响应式下按钮不再右对齐 */
  .export-btn {
    margin-left: 0 !important;
  }
  
  ::v-deep .el-date-editor.el-input,
  .el-input {
    width: 100% !important;
    margin-left: 0 !important;
    margin-bottom: 10px;
  }
  
  .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}
</style>
